import { Controller, useFormContext, useWatch } from 'react-hook-form';
import { Checkbox } from '@vnetwork/ui/primitives';
import { Field } from '@vnetwork/ui/forms';
import type { ContractFormValues } from '../contract-form';
import { NumericInput } from '@/components/numeric-input';

/** RHF-bound number input for a config-option sub-field (e.g. `<optPath>.commitment_value`). */
export function OptionNumberField({
  path,
  label,
  suffix,
  disabled,
}: {
  path: string;
  label: string;
  suffix?: string;
  disabled?: boolean;
}) {
  const { control } = useFormContext<ContractFormValues>();
  return (
    <Controller
      control={control}
      name={path as never}
      render={({ field }) => (
        <Field label={label}>
          {(p) => (
            <NumericInput
              {...p}
              disabled={disabled}
              endSlot={suffix ? <span style={{ fontSize: 'var(--vn-font-size-xs)' }}>{suffix}</span> : undefined}
              value={field.value == null ? '' : String(field.value)}
              onChange={(e) => field.onChange(e.target.value === '' ? undefined : Number(e.target.value))}
              onBlur={field.onBlur}
              name={field.name}
              ref={field.ref}
            />
          )}
        </Field>
      )}
    />
  );
}

/**
 * Commitment fields for a bandwidth/config option in `commit` form: value,
 * price-per-unit, computed total (value × per-unit), prepaid checkbox and
 * commitment time. Renders nothing when the product is `not_commit`.
 */
export function CommitmentFields({ optPath }: { optPath: string }) {
  const { control, setValue } = useFormContext<ContractFormValues>();
  const value = Number(useWatch({ control, name: `${optPath}.commitment_value` as never }) ?? 0);
  const perUnit = Number(useWatch({ control, name: `${optPath}.commitment_price_per_unit` as never }) ?? 0);
  const total = value * perUnit;

  return (
    <div
      style={{
        display: 'grid',
        gap: 'var(--vn-space-3)',
        gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))',
      }}
    >
      <OptionNumberField path={`${optPath}.commitment_value`} label="Commitment value" suffix="GB" />
      <OptionNumberField path={`${optPath}.commitment_price_per_unit`} label="Price per unit" />
      <Field label="Commitment total">
        {(p) => <NumericInput {...p} disabled value={String(total)} readOnly />}
      </Field>
      <OptionNumberField path={`${optPath}.commitment_time`} label="Commitment time (months)" />
      <Controller
        control={control}
        name={`${optPath}.commitment_prepaid` as never}
        render={({ field }) => (
          <Field label="Prepaid">
            {() => (
              <Checkbox
                checked={field.value === true}
                onCheckedChange={(v) =>
                  setValue(`${optPath}.commitment_prepaid` as never, (v === true) as never, { shouldDirty: true })
                }
              />
            )}
          </Field>
        )}
      />
    </div>
  );
}
